import React, { useCallback, useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import axios from "axios";

import { ConeLogisticoFiltroAcesso } from "../../../Models/Paineis/ConeLogistico/ConeLogisticoFiltroAcesso";
import { ConeLogisticoFiltroAcessoService } from "../../../Services/Cadastros/ConeLogistico/ConeLogisticoFiltroAcessoService";

import InlineSearchComponent from "../../../Components/Forms/InlineSearchComponent";
import DataGridCrudComponent from "../../../Components/Forms/DataGridCrudComponent";
import DropDownListComponent from "../../../Components/Forms/DropDownListComponent";
import TextFieldComponent from "../../../Components/Forms/TextFieldComponent";
import DatePickerComponent from "../../../Components/Forms/DatePickerComponent";
import NumberFieldComponent from "../../../Components/Forms/NumberFieldComponent";

interface Props {
  coneLogisticoFiltroAcesso?: ConeLogisticoFiltroAcesso;
  coneLogisticoFiltroAcessos?: ConeLogisticoFiltroAcesso[];
  setConeLogisticoFiltroAcessos: (val: ConeLogisticoFiltroAcesso[]) => void;
}

const ConeLogisticoFiltroAcessoPartial: React.FC<Props> = ({
  coneLogisticoFiltroAcesso,
  coneLogisticoFiltroAcessos,
  setConeLogisticoFiltroAcessos
}) => {
  const [filtroAcesso, setFiltroAcesso] = useState<ConeLogisticoFiltroAcesso>(new ConeLogisticoFiltroAcesso());
  const [filtroSelecionado, setFiltroSelecionado] = useState<ConeLogisticoFiltroAcesso>(new ConeLogisticoFiltroAcesso());
  const [listaFiltros, setListaFiltros] = useState<ConeLogisticoFiltroAcesso[]>([]);
  const [regionais, setRegionais] = useState<any[]>([]);
  const [ufs, setUfs] = useState<any[]>([]);
  const [viagemTipos, setViagemTipos] = useState<any[]>([]);
  const [preferences] = useState(() => {
    try {
      const stored = localStorage.getItem("preferences");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const filtroAcessoService = new ConeLogisticoFiltroAcessoService();

  const colunas: GridColDef[] = [
    { field: "regional", headerName: "Regional", flex: 0.2, minWidth: 200 },
    { field: "operacao", headerName: "Operação", flex: 0.2, minWidth: 200 },
    { field: "origem", headerName: "Origem", flex: 0.1, minWidth: 200 },
    { field: "destino", headerName: "Destino", flex: 0.1, minWidth: 200 }
  ];

    useEffect(() => {
        const loadCombo = async () => {
            try {
                const regionalCache = sessionStorage.getItem("comboRegional");
                const ufsCache = sessionStorage.getItem("comboEstados");
                const tiposCache = sessionStorage.getItem("comboTipos");

                if (regionalCache) {
                    setRegionais(JSON.parse(regionalCache));
                }

                if (ufsCache) {
                    setUfs(JSON.parse(ufsCache));
                }

                if (tiposCache) {
                    setViagemTipos(JSON.parse(tiposCache));
                }

                const promises: Promise<any>[] = [];
                promises.push(...(!regionalCache ? [axios.get("buscaComboRegional")] : []));
                promises.push(...(!ufsCache ? [axios.get("buscaEstados")] : []));
                promises.push(...(!tiposCache ? [axios.get("viagem/tipos")] : []));

                if (promises.length === 0) {
                    return;
                }

                const responses = await Promise.all(promises);

                let i = 0;
                if (!regionalCache) {
                    const data = responses[i++].data ?? [];
                    setRegionais(data);
                    sessionStorage.setItem("comboRegional", JSON.stringify(data));
                }

                if (!ufsCache) {
                    const data = responses[i++].data ?? [];
                    setUfs(data);
                    sessionStorage.setItem("comboEstados", JSON.stringify(data));
                }

                if (!tiposCache) {
                    const data = responses[i++].data ?? [];
                    setViagemTipos(data);
                    sessionStorage.setItem("comboTipos", JSON.stringify(data));
                }
            } catch {
                enqueueSnackbar("Erro ao carregar dados iniciais", { variant: "error" });
            }
        };

        loadCombo();
    }, [enqueueSnackbar]);


  useEffect(() => {
    setFiltroAcesso(coneLogisticoFiltroAcesso ?? new ConeLogisticoFiltroAcesso());
    if (!coneLogisticoFiltroAcessos) {
        return;
    }

    const data = filtroAcessoService.selecionarValidos(coneLogisticoFiltroAcessos);
    setListaFiltros(data ?? []);
  }, [coneLogisticoFiltroAcesso, coneLogisticoFiltroAcessos, filtroAcessoService]);

  const editar = useCallback((id: number) => {
    const entity = listaFiltros.find(e => e.id === id);
    if (entity) {
      setFiltroSelecionado({ ...entity });
    }
    else {
      console.warn(`Nenhum registro encontrado com ID: ${id}`);
    }
  }, [listaFiltros]);

  const cancelar = useCallback(() => {
    setFiltroSelecionado(new ConeLogisticoFiltroAcesso());
  }, []);

  const salvar = useCallback(() => {
    const response = filtroAcessoService.salvar(filtroSelecionado, coneLogisticoFiltroAcessos);
    if (!response.valid) {
      enqueueSnackbar(response.message, { variant: "error" });
      return;
    }

    setConeLogisticoFiltroAcessos(response.entities);
    cancelar();
    enqueueSnackbar("Registro salvo com sucesso!", { variant: "success" });
  }, [filtroSelecionado, coneLogisticoFiltroAcessos, cancelar, enqueueSnackbar, filtroAcessoService, setConeLogisticoFiltroAcessos]);

  const apagar = useCallback(async (id: number) => {
    try {
      await confirm({ title: "Deseja realmente apagar este registro?" });

      const response = filtroAcessoService.apagar(id, coneLogisticoFiltroAcessos);
      if (!response.valid) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }

      setConeLogisticoFiltroAcessos(response.entities);
      enqueueSnackbar("Registro apagado com sucesso!", { variant: "success" });
    } catch {
    }
  }, [confirm, coneLogisticoFiltroAcessos, filtroAcessoService, enqueueSnackbar, setConeLogisticoFiltroAcessos]);

  return (
    <>
      <div className="row area-fitro">
        <DatePickerComponent
          divClassName="col-xl-2 col-lg-3 col-md-6 col-sm-6 col-12 mb-2"
          className="col-12 col-md-12"
          label="Data Inicial"
          required
          model={filtroAcesso}
          field="data_inicial"
        />

        <DatePickerComponent
          divClassName="col-xl-2 col-lg-3 col-md-6 col-sm-6 col-12 mb-2"
          className="col-12 col-md-12"
          label="Data Final"
          required
          model={filtroAcesso}
          field="data_final"
        />

        <TextFieldComponent
          label="Placa"
          field="placa"
          id="placa"
          divClassName="col-xl-2 col-lg-2 col-md-3 col-sm-3 col-6 mb-2"
          variant="outlined"
          model={filtroAcesso}
        />

        <TextFieldComponent
          divClassName="col-xl-2 col-lg-2 col-md-3 col-sm-3 col-6 mb-2"
          label="Frota"
          field="frota"
          id="frota"
          variant="outlined"
          model={filtroAcesso}
        />

        <DropDownListComponent
          divClassName="col-xl-2 col-lg-2 col-md-2 col-sm-6 col-6 mb-2"
          label="UF"
          required
          options={ufs}
          valueField="id"
          textField="sigla"
          model={filtroAcesso}
          modelField="uf_id"
        />

        <DropDownListComponent
          divClassName="col-xl-2 col-lg-3 col-md-4 col-sm-6 col-6 mb-2"
          label="Tipo Viagem"
          options={viagemTipos}
          valueField="id"
          textField="descricao"
          model={filtroAcesso}
          modelField="viagem_tipo_id"
        />

        <InlineSearchComponent
          divClassName="col-xl-4 col-lg-3 col-md-5 col-sm-6 col-12 mb-2"
          id="combo_empresa_operacao"
          label="Empresa Operação"
          urlApi="buscaComboEmpresas"
          valueField="id"
          textField="nome_completo"
          model={filtroAcesso}
          modelField="operacao_empresa_id"
        />

        <InlineSearchComponent
          divClassName="col-xl-6 col-lg-4 col-md-5 col-sm-9 col-8 mb-2"
          id="combo_empresa_raio"
          label="Empresa Raio"
          urlApi="buscaComboEmpresas"
          valueField="id"
          textField="nome_completo"
          model={filtroAcesso}
          modelField="raio_empresa_id"
        />

        <NumberFieldComponent
          divClassName="col-xl-2 col-lg-2 col-md-2 col-sm-3 col-4 mb-2"
          label="Raio(km)"
          field="raio_km"
          id="raio_km"
          variant="outlined"
          model={filtroAcesso}
        />
      </div>

      <hr />
      <h1 className="h4" style={{ color: preferences.darkMode ? "white" : "black" }}>
        Dados listagem
      </h1>

      <DataGridCrudComponent
        model={filtroSelecionado}
        save={salvar}
        cancel={cancelar}
        labelSave="Salvar"
        gridEdit={editar}
        gridDelete={apagar}
        gridRows={listaFiltros}
        gridColumns={colunas}
        gridHideFooter
        gridFieldId="id"
        gridHeight={500}
      >
        <div className="col-md-3 mb-3 col-12">
          <DropDownListComponent
            label="Regional"
            required
            options={regionais}
            valueField="regional"
            textField="regional"
            model={filtroSelecionado}
            modelField="regional"
          />
        </div>

        <div className="col-md-3 mb-2 col-12">
          <InlineSearchComponent
            id="combo_operacao"
            label="Operação"
            urlApi="buscaOperacoes"
            valueField="id"
            textField="nome_completo"
            model={filtroSelecionado}
            modelField="operacao_id"
            modelFieldName="operacao"
          />
        </div>

        <div className="col-md-3 mb-2 col-12">
          <InlineSearchComponent
            id="combo_empresa_origem"
            label="Origem"
            urlApi="buscaComboEmpresas"
            valueField="id"
            textField="nome_completo"
            model={filtroSelecionado}
            modelField="origem_id"
            modelFieldName="origem"
          />
        </div>

        <div className="col-md-3 mb-2 col-12">
          <InlineSearchComponent
            id="combo_empresa_destino"
            label="Destino"
            urlApi="buscaComboEmpresas"
            valueField="id"
            textField="nome_completo"
            model={filtroSelecionado}
            modelField="destino_id"
            modelFieldName="destino"
          />
        </div>
      </DataGridCrudComponent>
    </>
  );
};

export default React.memo(ConeLogisticoFiltroAcessoPartial);
